const IMPRESSION_TYPE_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-clinical-impression-type';
export const IMPRESSION_CITATION_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-citations';
const IMPRESSION_CITATION_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-citation-id';

export const IMPRESSION_AI_GENERATED =
  'https://lifeomic.com/fhir/fountain-life-ai-generated';

const TEMPLATE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-impression-template-id';

const TEMPLATE_DURABLE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-impression-template-durable-id';

const TRACE_ID_SYSTEM =
  'http://lifeomic.com/fhir/fountain-life-ai-generated-trace-id';

export const REVIEW_DIFF_DOCUMENT_SYSTEM =
  'https://lifeomic.com/fhir/review-diff-document';

export const REVIEW_PROPOSED_CITATIONS_SYSTEM =
  'https://lifeomic.com/fhir/review-proposed-citations';

export const SUGGESTION_RATIONALE_SYSTEM =
  'https://lifeomic.com/fhir/zori-suggestion-rationale';

export { REVIEW_BASE_FINGERPRINT_URL as REVIEW_BASE_FINGERPRINT_SYSTEM } from '../../../../../util/contentFingerprint';

import { Endpoints } from '@lifeomic/react-client/generated/ai-template-service';

import moment from 'moment';
import { v4 } from 'uuid';
import {
  SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
  SYSTEM_LAST_EDITED_BY,
} from '../../../../../util/fhir';
import { EXECUTIVE_SUMMARY_IMPRESSION_TYPES } from '../../ExecutiveSummary/constants';
import { isAiGenerated } from '../../ExecutiveSummary/utils';
import {
  getCitationIdPrefix,
  isDocumentReference,
  isFhirReference,
} from '../AttachmentArbiter/Attachment';
import { stripCitationMarkersFromMarkdown } from './MarkdownEditor/stripCitationMarkdown';
import { FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION } from '../../../../../util/zoriEditingExtensions';

/** These are all the kinds we have iconography/text for */
export const KNOWN_IMPRESSION_TYPES: readonly NonNullable<
  Extract<
    Endpoints['GET /v1/ai-templates/:id']['Response']['template'],
    { type: 'template' }
  >['impressionType']
>[] = [
  ...EXECUTIVE_SUMMARY_IMPRESSION_TYPES,
  'general',
  'synopsis',
  'nutrition',
  'supplements',
];

export type KnownImpressionTypes = (typeof KNOWN_IMPRESSION_TYPES)[number];

export class ImpressionItem {
  constructor(public impression: fhir.ClinicalImpression) {}

  get id() {
    return this.impression.id!;
  }

  get title() {
    return this.impression.description;
  }

  get summary() {
    return this.impression.summary;
  }

  get findings() {
    return (this.impression.finding ?? []).map(
      finding => finding.itemReference?.display ?? '',
    );
  }

  get attachments() {
    return (
      this.impression.investigation?.find(
        investigation =>
          investigation.code.text === IMPRESSION_CITATION_SYSTEM ||
          investigation.code.coding?.some(
            coding => coding.system === IMPRESSION_CITATION_SYSTEM,
          ),
      )?.item ?? []
    );
  }

  get templateId() {
    // look for the template durable id first, then fall back to the template id
    return (
      this.impression.meta?.tag?.find(
        tag => tag.system === TEMPLATE_DURABLE_ID_SYSTEM,
      )?.code ??
      this.impression.meta?.tag?.find(tag => tag.system === TEMPLATE_ID_SYSTEM)
        ?.code ??
      ''
    );
  }

  get status() {
    return this.impression.status;
  }

  get lastEdited() {
    return this.impression.meta?.lastUpdated
      ? moment(this.impression.meta.lastUpdated).format('MM/DD/YYYY hh:mm A')
      : undefined;
  }

  get reportDate(): string | undefined {
    const date = this.impression.date ?? this.impression.meta?.lastUpdated;
    return date ? moment(date).format('MMM D YYYY') : undefined;
  }

  get lastEditedBy() {
    if (isAiGenerated(this.impression)) {
      return 'Zori';
    }
    return this.getLastEditedBy();
  }

  get aiGenerated() {
    return isAiGenerated(this.impression);
  }

  get traceId() {
    return this.impression.extension?.find(ext => ext.url === TRACE_ID_SYSTEM)
      ?.valueString;
  }

  get reviewDiffDocument(): string | undefined {
    return this.impression.extension?.find(
      ext => ext.url === REVIEW_DIFF_DOCUMENT_SYSTEM,
    )?.valueString;
  }

  get suggestionRationale(): { text: string; generatedAt: string } | undefined {
    const raw = this.impression.extension?.find(
      ext => ext.url === SUGGESTION_RATIONALE_SYSTEM,
    )?.valueString;
    if (raw == null) return undefined;
    try {
      const parsed = JSON.parse(raw) as {
        text?: unknown;
        generatedAt?: unknown;
      };
      if (
        typeof parsed.text !== 'string' ||
        typeof parsed.generatedAt !== 'string'
      ) {
        console.warn(
          '[ImpressionItem] Malformed suggestion-rationale extension; ignoring.',
          raw,
        );
        return undefined;
      }
      return { text: parsed.text, generatedAt: parsed.generatedAt };
    } catch {
      console.warn(
        '[ImpressionItem] Failed to parse suggestion-rationale extension; ignoring.',
        raw,
      );
      return undefined;
    }
  }

  get investigation(): fhir.ClinicalImpression['investigation'] {
    return this.impression.investigation;
  }

  /**
   * Returns the agent-staged citation list for the pending diff, or `undefined`
   * if no snapshot exists (e.g. diffs written before the snapshot extension
   * was introduced). An empty array is meaningful — it means the agent
   * explicitly cleared all citations.
   */
  get proposedCitations(): fhir.Reference[] | undefined {
    const raw = this.impression.extension?.find(
      ext => ext.url === REVIEW_PROPOSED_CITATIONS_SYSTEM,
    )?.valueString;
    if (raw == null) return undefined;
    try {
      return JSON.parse(raw) as fhir.Reference[];
    } catch {
      console.warn(
        '[ImpressionItem] Failed to parse review-proposed-citations extension; falling back to heuristic prune.',
        raw,
      );
      return undefined;
    }
  }

  /** Langfuse root trace for the suggest-agent run that produced `reviewDiffDocument`. */
  get suggestionTraceId(): string | undefined {
    const raw = this.impression.extension?.find(
      ext => ext.url === FOUNTAIN_SUGGESTION_LANGFUSE_TRACE_ID_EXTENSION,
    )?.valueString;
    const trimmed = raw?.trim();
    return trimmed ? trimmed : undefined;
  }

  addAttachment(
    attachment: fhir.Resource | fhir.Reference,
  ): fhir.Reference | undefined {
    let reference: fhir.Reference;
    if (isFhirReference(attachment)) {
      reference = { ...attachment };
    } else {
      const type = attachment.resourceType!;
      const id = attachment.id!;

      reference = {
        reference: `${type}/${id}`,
      };
    }

    const citationIdPrefix = getCitationIdPrefix(attachment);

    if (!citationIdPrefix) {
      console.error('Adding attachment type is not supported', attachment);
      return;
    }
    const nextId = `${citationIdPrefix}${v4()}`;

    reference = {
      ...reference,
      identifier: {
        system: IMPRESSION_CITATION_ID_SYSTEM,
        value: `${citationIdPrefix}${nextId}`,
      },
      extension: isDocumentReference(attachment)
        ? [
            {
              url: 'http://lifeomic.com/fhir/document-type',
              valueString: 'clinician-provided', // TODO: make this dynamic
            },
          ]
        : [],
    };

    const idx = this.impression.investigation?.findIndex(
      inv => inv.code.text === IMPRESSION_CITATION_SYSTEM,
    );

    // update the existing investigation if it exists
    if (idx !== -1 && idx !== undefined && this.impression.investigation) {
      this.impression.investigation[idx] = {
        ...this.impression.investigation[idx],
        item: [...(this.impression.investigation[idx].item || []), reference],
      };
    } else if (this.impression.investigation) {
      // add a new investigation if it doesn't exist, but there is another investigation
      this.impression.investigation.push({
        code: { text: IMPRESSION_CITATION_SYSTEM },
        item: [reference],
      });
    } else {
      // add a new investigation if it doesn't exist
      this.impression.investigation = [
        {
          code: { text: IMPRESSION_CITATION_SYSTEM },
          item: [reference],
        },
      ];
    }

    return reference;
  }

  get impressionType(): KnownImpressionTypes {
    return (
      this.impression.code?.coding?.find(
        c => c.system === IMPRESSION_TYPE_SYSTEM,
      )?.code ?? 'general'
    ).toLowerCase() as KnownImpressionTypes;
  }

  getAttachmentByIdentifier(identifier: string) {
    return this.attachments.find(attachment => {
      return (
        attachment.identifier?.system === IMPRESSION_CITATION_ID_SYSTEM &&
        attachment.identifier.value === identifier
      );
    });
  }

  /**
   * Used to deduplicate attachments with different identifiers and with the same reference.
   * Primarily used for expanded view, to prevent embedding the same attachment multiple times.
   * when the citation id is different, but points to the same attachment.
   * */
  getDeduplicatedAttachmentByIdentifier(identifier: string) {
    const deduplicatedAttachments = this.attachments.reduce(
      (acc, attachment) => {
        if (acc.findIndex(a => a.reference === attachment.reference) === -1) {
          acc.push(attachment);
        }
        return acc;
      },
      [] as fhir.Reference[],
    );
    return deduplicatedAttachments.find(attachment => {
      return (
        attachment.identifier?.system === IMPRESSION_CITATION_ID_SYSTEM &&
        attachment.identifier.value === identifier
      );
    });
  }

  getLastEditedBy() {
    return (
      // look for the last edited by extension, first in the new system, then in the old system
      this.impression.extension?.find(ext => ext.url === SYSTEM_LAST_EDITED_BY)
        ?.valueString ??
      this.impression.extension?.find(
        ext => ext.url === SYSTEM_ACTION_PLAN_LAST_EDITED_BY,
      )?.valueString ??
      ''
    );
  }

  /**
   * Mutates `impression.summary` to the stripped markdown so that
   * `StripCitationsPlugin` can reload the Lexical editor with citations removed.
   * Also strips citation markers from the review-diff extension (if any) and
   * removes FHIR investigation entries that hold citation attachments.
   *
   * NOTE: `saveImpl` in `ImpressionEditor/index.tsx` reads the fingerprint base
   * from `lastPersistedSummaryRef` (not from `impression.summary`), so this
   * in-place mutation cannot cause a `review-base-fingerprint` mismatch (409).
   */
  applyStrippedSummaryMarkdown(strippedSummaryMarkdown: string): void {
    this.impression.summary = strippedSummaryMarkdown;

    const revExt = this.impression.extension?.find(
      ext => ext.url === REVIEW_DIFF_DOCUMENT_SYSTEM,
    );
    if (revExt?.valueString != null) {
      const strippedDiff = stripCitationMarkersFromMarkdown(revExt.valueString);
      if (strippedDiff.trim() === '') {
        this.impression.extension = (this.impression.extension ?? []).filter(
          ext => ext.url !== REVIEW_DIFF_DOCUMENT_SYSTEM,
        );
      } else {
        revExt.valueString = strippedDiff;
      }
    }

    if (!this.impression.investigation?.length) {
      return;
    }
    this.impression.investigation = this.impression.investigation.filter(
      inv =>
        !(
          inv.code?.text === IMPRESSION_CITATION_SYSTEM ||
          inv.code?.coding?.some(
            coding => coding.system === IMPRESSION_CITATION_SYSTEM,
          )
        ),
    );
  }
}
